import type { LootCollectedFeedbackPayload } from './CombatFeedbackTypes';
import * as Phaser from 'phaser';
import { EventBus } from '../core/EventBus';
import PoolManager from '../managers/PoolManager';
import { PlayerStats } from '../components/PlayerStats';
import { createLogger } from '../utils/Logger';
import { ContentRegistry, EnemyDropContent, loadContentRegistry } from '../data';
import { BossEncounterEventMetadata } from './BossEventPayloads';

type RandomSource = () => number;

interface EnemyKilledPayload {
    id?: unknown;
    x: number;
    y: number;
    xpValue?: unknown;
    bossEncounter?: BossEncounterEventMetadata;
}

export class LootSystem {
    private poolManager: PoolManager;
    private eventBus: EventBus;
    private contentRegistry: ContentRegistry;
    private rng: RandomSource;
    private player: Phaser.Physics.Arcade.Sprite | null = null;
    private gems: Phaser.GameObjects.Group | undefined;

    // Magnet range
    private pickupRange: number = 100;
    private magnetSpeed: number = 300;
    private playerStats: PlayerStats | null = null;
    private readonly logger = createLogger('LootSystem');
    private readonly eventUnsubscribers: Array<() => void> = [];
    private isDisposed = false;

    constructor(
        poolManager: PoolManager,
        eventBus: EventBus,
        contentRegistry: ContentRegistry = loadContentRegistry(),
        rng: RandomSource = Math.random
    ) {
        this.poolManager = poolManager;
        this.eventBus = eventBus;
        this.contentRegistry = contentRegistry;
        this.rng = rng;

        this.subscribeEvents();
    }

    private subscribeEvents(): void {
        const onEnemyKilled = (event: any): void => {
            const data = event.data || event;
            if (!data || typeof data.x !== 'number' || typeof data.y !== 'number') {
                this.logger.warn('Invalid enemy:killed payload for loot resolution', {
                    source: 'enemy:killed', reason: 'invalid-position'
                });
                return;
            }

            const enemyId = data.id;
            if (typeof enemyId !== 'string' || enemyId.length === 0) {
                this.logger.warn('Missing enemy ID in enemy:killed payload; using XP fallback', {
                    enemyId,
                    x: data.x,
                    y: data.y
                });
                this.spawnFallbackGem(data);
                return;
            }

            this.resolveEnemyDrops(data as EnemyKilledPayload & { id: string });
        };

        this.eventBus.on('enemy:killed', onEnemyKilled);
        this.eventUnsubscribers.push(() => this.eventBus.off('enemy:killed', onEnemyKilled));
    }

    private resolveEnemyDrops(data: EnemyKilledPayload & { id: string }): void {
        const enemy = this.contentRegistry.findEnemy(data.id);
        if (!enemy) {
            this.logger.warn('Unknown enemy ID in enemy:killed payload; using XP fallback', {
                enemyId: data.id,
                x: data.x,
                y: data.y
            });
            this.spawnFallbackGem(data);
            return;
        }

        const encounterRewardHandled = this.resolveEncounterRewardPolicy(data, enemy);
        if (encounterRewardHandled) {
            return;
        }

        const drops = enemy.drops && enemy.drops.length > 0
            ? enemy.drops
            : [{ type: 'gem', chance: 1, value: enemy.stats.xpReward } satisfies EnemyDropContent];

        for (const drop of drops) {
            const roll = this.rng();
            const succeeded = roll < drop.chance;

            if (!succeeded) continue;

            if (drop.type === 'chest') {
                this.logger.info('Chest drop succeeded', {
                    enemyId: enemy.id,
                    x: data.x,
                    y: data.y
                });
                this.spawnChest(data.x, data.y);
                continue;
            }

            const value = this.resolveXpValue(data.xpValue, drop.value ?? enemy.stats.xpReward, 'enemyDrops');
            this.spawnGem(data.x, data.y, value);
        }
    }

    private resolveEncounterRewardPolicy(data: EnemyKilledPayload & { id: string }, enemy: { id: string; stats: { xpReward: number } }): boolean {
        const policy = data.bossEncounter?.rewardPolicy;
        if (!policy) {
            return false;
        }

        this.logger.debug('Encounter reward policy payload', {
            enemyId: data.id,
            encounterId: data.bossEncounter?.encounterId,
            rewardPolicy: policy
        });

        switch (policy.mode) {
            case 'enemyDrops':
                this.logger.debug('Encounter reward policy delegates to enemy drops', {
                    enemyId: data.id,
                    encounterId: data.bossEncounter?.encounterId
                });
                return false;
            case 'guaranteedChest': {
                const chestCount = policy.chestCount;
                if (typeof chestCount !== 'number' || !Number.isInteger(chestCount) || chestCount <= 0) {
                    this.logger.warn('Malformed encounter reward policy (guaranteedChest), falling back to enemy drops', {
                        enemyId: data.id,
                        encounterId: data.bossEncounter?.encounterId,
                        rewardPolicy: policy
                    });
                    return false;
                }

                for (let index = 0; index < chestCount; index += 1) {
                    this.spawnChest(data.x, data.y);
                }
                this.logger.info('Encounter reward policy applied: guaranteedChest', {
                    enemyId: data.id,
                    encounterId: data.bossEncounter?.encounterId,
                    chestCount,
                    x: data.x,
                    y: data.y
                });
                return true;
            }
            case 'guaranteedGems': {
                const gemMultiplier = policy.gemMultiplier;
                if (typeof gemMultiplier !== 'number' || !Number.isFinite(gemMultiplier) || gemMultiplier <= 0) {
                    this.logger.warn('Malformed encounter reward policy (guaranteedGems), falling back to enemy drops', {
                        enemyId: data.id,
                        encounterId: data.bossEncounter?.encounterId,
                        rewardPolicy: policy
                    });
                    return false;
                }

                const baseXp = this.resolveXpValue(data.xpValue, enemy.stats.xpReward, 'guaranteedGems');
                const rewardValue = baseXp * gemMultiplier;
                this.spawnGem(data.x, data.y, rewardValue);
                this.logger.info('Encounter reward policy applied: guaranteedGems', {
                    enemyId: data.id,
                    encounterId: data.bossEncounter?.encounterId,
                    gemMultiplier,
                    baseXp,
                    rewardValue,
                    x: data.x,
                    y: data.y
                });
                return true;
            }
            default:
                this.logger.warn('Unknown encounter reward policy mode, falling back to enemy drops', {
                    enemyId: data.id,
                    encounterId: data.bossEncounter?.encounterId,
                    rewardPolicy: policy
                });
                return false;
        }
    }

    private spawnFallbackGem(data: EnemyKilledPayload): void {
        const value = this.resolveXpValue(data.xpValue, 10, 'unknownEnemy');
        this.spawnGem(data.x, data.y, value);
    }

    private resolveXpValue(value: unknown, fallback: number, source: string): number {
        const valid = typeof value === 'number' && Number.isFinite(value) && value >= 0;
        if (!valid && value !== undefined) {
            this.logger.warn('Invalid XP value; using fallback', {
                source, valueType: typeof value, reason: 'expected-finite-nonnegative-number'
            });
        }
        const selected = valid ? value : fallback;
        this.logger.debug('XP source selected', { source, usedFallback: !valid, value: selected });
        return selected;
    }

    public spawnChest(x: number, y: number): void {
        const chest = this.poolManager.spawn(x, y, 'chest');
        if (chest) {
            chest.setTint(0xffd700); // Gold tint for now if no texture
            chest.setData('type', 'chest');
            chest.setData('visualAge', 0);
            chest.setVelocity(0, 0);
        }
    }

    // ... (rest of file)

    public init(player: Phaser.Physics.Arcade.Sprite, stats?: PlayerStats): void {
        this.player = player;
        if (stats) {
            this.setPlayerStats(stats);
        }
        this.gems = this.poolManager.getGroup('gem');
        this.logger.info('Initialized loot system', {
            hasPlayer: !!player,
            hasStats: !!stats,
            hasContentRegistry: !!this.contentRegistry,
            pickupRange: this.pickupRange
        });
        this.logger.debug('Resolved enemy drop tables', {
            enemies: this.contentRegistry.getEnemies().map(enemy => ({
                enemyId: enemy.id,
                drops: enemy.drops && enemy.drops.length > 0
                    ? enemy.drops.map(drop => ({
                        type: drop.type,
                        chance: drop.chance,
                        value: drop.type === 'gem' ? drop.value ?? enemy.stats.xpReward : undefined
                    }))
                    : [{ type: 'gem', chance: 1, value: enemy.stats.xpReward }]
            }))
        });
    }

    public setPlayerStats(stats: PlayerStats): void {
        this.playerStats = stats;
        this.refreshPickupRange();
    }

    public refreshPickupRange(): void {
        if (!this.playerStats) return;

        const nextRange = this.playerStats.get('pickupRange');
        if (nextRange !== this.pickupRange) {
            this.logger.debug('Pickup range updated from player stats', {
                previous: this.pickupRange,
                next: nextRange
            });
            this.pickupRange = nextRange;
        }
    }

    public spawnGem(x: number, y: number, value: number): void {
        // Using a generic 'gem' type for now, could be 'gem_blue', 'gem_red' etc based on value
        const gem = this.poolManager.spawn(x, y, 'gem');

        if (gem) {
            // Store the XP value on the gem object
            gem.setData('value', value);
            gem.setData('visualAge', 0);
            gem.setVelocity(0, 0); // Stops momentum if any
        }
    }

    public update(_dt: number): void {
        if (!this.player) return;
        this.refreshPickupRange();

        // Handle Gems
        if (this.gems) {
            const activeGems = this.gems.getChildren() as Phaser.Physics.Arcade.Sprite[];
            activeGems.forEach(gem => {
                if (!gem.active) return;
                const age = Number(gem.getData('visualAge') ?? 0) + _dt / 1000;
                gem.setData('visualAge', age);
                gem.setAlpha?.(Math.min(1, 0.4 + age * 4));
                gem.setRotation?.(Math.sin(age * 3) * 0.12);
                const dist = Phaser.Math.Distance.Between(this.player!.x, this.player!.y, gem.x, gem.y);
                if (dist <= this.pickupRange) {
                    if (dist < 20) {
                        this.collectGem(gem);
                    } else {
                        this.poolManager.getScene()?.physics.moveToObject(gem, this.player!, this.magnetSpeed);
                    }
                } else {
                    gem.setVelocity(0, 0);
                }
            });
        }

        // Handle Chests (Separate group or check type?)
        // For now assuming chests are in 'chest' pool
        const chests = this.poolManager.getGroup('chest');
        if (chests) {
            const activeChests = chests.getChildren() as Phaser.Physics.Arcade.Sprite[];
            activeChests.forEach(chest => {
                if (!chest.active) return;
                const age = Number(chest.getData('visualAge') ?? 0) + _dt / 1000;
                chest.setData('visualAge', age);
                chest.setAlpha?.(Math.min(1, 0.4 + age * 4));
                chest.setRotation?.(Math.sin(age * 2) * 0.03);
                const dist = Phaser.Math.Distance.Between(this.player!.x, this.player!.y, chest.x, chest.y);
                if (dist < 30) { // Close range for chest
                    this.collectChest(chest);
                }
            });
        }
    }

    private collectGem(gem: Phaser.Physics.Arcade.Sprite): void {
        const value = this.resolveXpValue(gem.getData('value'), 10, 'gemPickup');
        this.eventBus.publish({ type: 'loot:collected', data: { x: gem.x, y: gem.y, kind: 'gem' } satisfies LootCollectedFeedbackPayload });
        this.poolManager.despawn(gem);
        this.eventBus.publish({
            type: 'xp:collected',
            data: value
        });
    }

    private collectChest(chest: Phaser.Physics.Arcade.Sprite): void {
        this.eventBus.publish({ type: 'loot:collected', data: { x: chest.x, y: chest.y, kind: 'chest' } satisfies LootCollectedFeedbackPayload });
        this.poolManager.despawn(chest);
        this.eventBus.publish({ type: 'chest:opened', data: {} });
    }

    public dispose(): void {
        if (this.isDisposed) {
            this.logger.warn('dispose:already-disposed');
            return;
        }

        this.eventUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
        this.isDisposed = true;
        this.logger.debug('dispose:complete');
    }
}
